import { dataAPI } from './dataApi';
import { normalizeItemLinkFieldsForSave } from '@/utils/linkFieldValue';

// Helper function to convert timestamp values to ISO strings
// Only converts values for fields that are defined as date/datetime types
const convertTimestampsToISO = (data: any, fields?: any[]): any => {
    const processedData = { ...data };

    // Create a map of field name to field type for quick lookup
    const fieldTypeMap = new Map<string, string>();
    if (fields && Array.isArray(fields)) {
        fields.forEach((field: any) => {
            if (field.name && field.type) {
                fieldTypeMap.set(field.name, field.type);
            }
        });
    }

    // Date field types that should be converted from timestamp to ISO string
    const dateFieldTypes = ['datetime', 'dateonly', 'timeonly', 'calendar'];

    for (const key in processedData) {
        const value = processedData[key];

        // Only convert if:
        // 1. Value is a number
        // 2. Field is defined and has a date type
        const fieldType = fieldTypeMap.get(key);
        const isDateField = fieldType && dateFieldTypes.includes(fieldType);

        if (typeof value === 'number' && !isNaN(value) && isDateField) {
            // Convert seconds timestamp to ISO string
            const date = new Date(value * 1000);
            // Only convert if the resulting date is valid
            if (!isNaN(date.getTime())) {
                processedData[key] = date.toISOString();
                console.log(`[convertTimestampsToISO] Converted ${fieldType} field "${key}" timestamp ${value} (${date.toLocaleDateString()}) to ISO string: ${processedData[key]}`);
            }
        }
    }
    return processedData;
};

const stripEmptyKeys = (data: any): any => {
    if (!data || typeof data !== 'object') {
        return data;
    }
    const cleaned: Record<string, any> = {};
    Object.entries(data).forEach(([key, value]) => {
        const normalizedKey = typeof key === 'string' ? key.trim() : key;
        if (normalizedKey) {
            cleaned[normalizedKey as string] = value;
        }
    });
    return cleaned;
};

// Uses slug (e.g., 'bookings', 'inventory') to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
export const createNewItem = async (
    slug: string,
    initialData: Record<string, any> = {},
    fields?: any[],
    user?: any,
    organization?: string | null
) => {
    return new Promise<any>(async (resolve, reject) => {
        try {
            // Convert timestamp values to ISO strings before sending to API
            // Only converts fields that are defined as date/datetime types
            const processedData = convertTimestampsToISO(initialData, fields);
            const { data: linkNormalized, error: linkError } =
                normalizeItemLinkFieldsForSave(processedData, fields);
            if (linkError) {
                reject(new Error(linkError));
                return;
            }
            const payload = stripEmptyKeys({
                ...linkNormalized,
                _created: linkNormalized._created || new Date(),
                _updated: new Date()
            });

            // Create the item directly with all form data
            // The backend API will automatically create the collection if it doesn't exist
            console.log('📝 createNewItem: Creating item with form data (backend will auto-create collection if needed)');
            const response = await dataAPI.createCollectionItem(slug, payload, organization);
            console.log('✅ createNewItem: Item created successfully');
            resolve(response.data || payload);
        }
        catch (error: any) {
            console.error('❌ createNewItem: Failed to create item:', error);
            reject(error);
        }
    });
}

// Uses slug (e.g., 'bookings', 'inventory') to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
export const itemUpdate = (
    itemData: any,
    slug: string,
    fields?: any[],
    user?: any,
    options?: { isNew?: boolean; organization?: string }
) => {
    return new Promise<any>(async (resolve, reject) => {
        try {
            if (!slug) {
                throw new Error('Slug is required to update an item.');
            }

            // Convert timestamp values (in seconds) to ISO date strings for MongoDB
            // Only converts fields that are defined as date/datetime types
            // This ensures dates are stored correctly in the database
            const processedData = convertTimestampsToISO(itemData, fields);
            const { data: linkNormalized, error: linkError } =
                normalizeItemLinkFieldsForSave(processedData, fields);
            if (linkError) {
                reject(new Error(linkError));
                return;
            }

            const payload = stripEmptyKeys({
                ...linkNormalized,
                _updated: new Date()
            });

            if (options?.isNew) {
                const createdItem = await createNewItem(slug, payload, fields, user, options?.organization);
                resolve({ data: createdItem, item: createdItem, followUpNotifications: null });
                return;
            }

            // PUT requests SHOULD use 'objectId' or '_id' field for the URL identifier
            // For MongoDB items, _id contains the objectId
            // Check for objectId in various possible locations (prioritize objectId and _id over uid)
            let itemIdentifier = payload.objectId
                || payload._id  // MongoDB uses _id as objectId
                || payload._objectId
                || (payload as any).object_id
                || (payload as any)._object_id
                || payload.uid;  // Fallback to uid if objectId/_id not available

            // Log the item structure for debugging
            console.log('🔍 itemUpdate: Item identifier check:', {
                hasObjectId: !!payload.objectId,
                has_id: !!payload._id,
                hasUid: !!payload.uid,
                hasId: !!(payload as any).id,
                objectIdValue: payload.objectId,
                _idValue: payload._id,
                uidValue: payload.uid,
                allKeys: Object.keys(payload)
            });

            // For new items without an identifier, create the item first
            if (!itemIdentifier) {
                console.log('📝 itemUpdate: No identifier found - creating new item via createNewItem');
                const createdItem = await createNewItem(slug, payload, fields, user, options?.organization);
                console.log('✅ itemUpdate: Created new item:', createdItem);
                resolve({ item: createdItem, followUpNotifications: null });
                return;
            }

            // If we only have uid, but not objectId/_id, try to load the full item
            // so we can preserve and reuse its database identifier for the PUT URL.
            if (itemIdentifier === payload.uid && !payload.objectId && !payload._id && slug && payload.uid) {
                try {
                    console.log('ℹ️ itemUpdate: Resolving objectId/_id via getCollectionItem before PUT');
                    const fullItemResponse = await dataAPI.getCollectionItem(slug, payload.uid, options?.organization);
                    const fullItem = fullItemResponse?.data || (fullItemResponse as any).item;

                    if (fullItem && typeof fullItem === 'object') {
                        const apiIdentifier =
                            fullItem.objectId ||
                            fullItem._id ||
                            fullItem._objectId ||
                            (fullItem as any).object_id ||
                            (fullItem as any)._object_id;

                        if (apiIdentifier) {
                            // Preserve identifiers from API response on the payload
                            payload.objectId = payload.objectId || apiIdentifier;
                            if (!payload._id && fullItem._id) {
                                payload._id = fullItem._id;
                            }

                            itemIdentifier = apiIdentifier;
                            console.log('✅ itemUpdate: Resolved objectId/_id from API for PUT request:', itemIdentifier);
                        } else {
                            console.warn('⚠️ itemUpdate: getCollectionItem response missing objectId/_id, continuing with uid.');
                        }
                    } else {
                        console.warn('⚠️ itemUpdate: getCollectionItem returned no item data, continuing with uid.');
                    }
                } catch (lookupError) {
                    console.error('⚠️ itemUpdate: Failed to resolve objectId/_id via getCollectionItem, continuing with uid.', lookupError);
                }
            }

            // Warn if we're still using uid instead of objectId/_id after attempting resolution
            if (itemIdentifier === payload.uid && !payload.objectId && !payload._id) {
                console.warn('⚠️ itemUpdate: Using uid as identifier because objectId/_id is missing.');
                console.warn('⚠️ itemUpdate: PUT requests should use objectId or _id. Item may need to preserve _id from API response.');
                console.warn('⚠️ itemUpdate: Using uid:', itemIdentifier);
            } else if (itemIdentifier === payload.objectId || itemIdentifier === payload._id) {
                console.log('✅ itemUpdate: Using objectId/_id for PUT request:', itemIdentifier);
                if (payload.uid && itemIdentifier !== payload.uid) {
                    console.log('   (uid was different:', payload.uid, ')');
                }
            }

            // Update existing item via PUT request
            const response = await dataAPI.updateCollectionItem(slug, itemIdentifier, payload, options?.organization);

            resolve({
                item: response?.data || payload,
                followUpNotifications: response?.followUpNotifications || null
            });
        } catch (error) {
            reject(error);
        };
    });
}

// Uses slug from itemData to identify the component
// The backend resolves the slug to the appropriate collection based on component configuration
export const itemDelete = (itemData: any) => {
    return new Promise<any>(async (resolve, reject) => {
        try {
            // Use slug from itemData, fallback to collectionName for backward compatibility
            const slug = itemData.slug || itemData.collectionName;
            if (!slug) {
                throw new Error('Slug or collectionName is required to delete an item.');
            }
            const response = await dataAPI.deleteCollectionItem(slug, itemData.uid, itemData.organization);
            resolve(response.data || itemData);
        } catch (error) {
            console.error('itemDelete', error);
            reject(error);
        };
    });
}

export const itemDeleteBulk = (payload: {
    slug?: string;
    collectionName?: string;
    uids: string[];
    organization?: string;
}) => {
    return new Promise<any>(async (resolve, reject) => {
        try {
            const slug = payload.slug || payload.collectionName;
            if (!slug) {
                throw new Error('Slug or collectionName is required for bulk delete.');
            }
            if (!Array.isArray(payload.uids) || payload.uids.length === 0) {
                resolve({ deletedCount: 0, deletedIds: [] });
                return;
            }
            const response = await dataAPI.deleteCollectionItemsBulk(slug, payload.uids, payload.organization);
            resolve({
                deletedCount: response?.deletedCount ?? 0,
                deletedIds: response?.deletedIds ?? [],
                errors: response?.errors
            });
        } catch (error) {
            console.error('itemDeleteBulk', error);
            reject(error);
        }
    });
}